import { isNaN } from 'lodash';
import {physicalAdapter} from "./physical";

export type ParcelType = "inbound" | "return" | "outbound";

export interface CarrierBarcode {
    carrier: "dpd" | "gls" | "ups" | "dhl" | "physical";
    destZipCode: string;
    destCountry: string;
    parcelNo: string;
    type: ParcelType;
}

export function parseBarcode(binary: string): CarrierBarcode | undefined {
    for (const parser of [parseDpdCode128, parseGlsPrimary2dCode, parseGlsUniShipCode, parseUps1Z3ZBarcode, parseUpsPTNBarcode, parseDhlNumberBarcode, parseDhlAlphanumericBarcode, parsePhysIcalContainerUrl]) {
        const result = parser(binary);
        if (result) {
            return result;
        }
    }
}

const DPD_BARCODE_PATTERN = /^[0-9]{27}$/;

export function parseDpdCode128(binary: string) {
    if (binary.startsWith("%")) {
        binary = binary.substr(1);
    }
    if (!DPD_BARCODE_PATTERN.test(binary)) {
        return undefined;
    }

    const destZipCode = binary.substr(0, 7);
    const parcelNo = binary.substr(7, 14);
    const service = binary.substr(21, 3);
    const destCountry = binary.substr(24, 3);

    const type = getDpdType(parcelNo, service);

    return {
        carrier: "dpd",
        destZipCode,
        parcelNo,
        type,
        destCountry
    } as const;
}

function getDpdType(parcelNo: string, service: string) {
    if (service == "328" && parcelNo.startsWith("06425")) {
        return "outbound";
    }
    if (service == "332") {
        return "return"; // nice :)
    }
    return "inbound";
}

const GLS_UNIT_NO_PATTERN = /^[0-9A-Z]{8}$/;

function calculateGlsTrackId(parcelId: string) {
    let trackId: string | null = null;
    if (parcelId) {
        const sBase: number = 36; // source base 36
        const dBase = 10;

        try {
            const numericTrackId = parseInt(parcelId, dBase) + 2721109907456;
            trackId = numericTrackId.toString(sBase).toUpperCase();
        } catch (error) {
            console.error(error);
        }
    }
    return trackId;
}

export function parseGlsPrimary2dCode(binary: string) {
    // example:
    // "AAT 530AT 3100401219179040aa2ehBTZEH8KN0DBBv         1   10101010   00100001001000100002                                   "

    // length must be exactly 123 bytes
    // first char is the version field. it must contain "A"
    if (binary.length != 123 || !binary.startsWith("A")) {
        return undefined;
    }

    const unitNo = binary.substr(33, 8);
    if (!GLS_UNIT_NO_PATTERN.test(unitNo)) {
        return undefined;
    }

    const destZipCode = binary.substr(61, 7).trim();
    const destCountry = binary.substr(7, 2);

    const services = binary.substr(43, 10);
    const outbound = services.includes("v") || services.includes("d");

    if (outbound) {
        // outbound parcels are not supported for gls anymore
        return undefined;
    }

    return {
        carrier: "gls",
        // A1 wants to query sender data even for outbound parcels where the sender is already known to GLS.
        // This will probably change back to "outbound" later, but here we go with "return":
        // type: outbound ? "outbound" : "inbound",
        type: outbound ? "return" : "inbound",
        destZipCode,
        destCountry,
        parcelNo: unitNo,
    } as const;
}

export function parseGlsUniShipCode(binary: string) {
    // Example: "A|0400800319|0400008244|AA|40|8141|001|001|53766050030|GLS Austria GmbH|Depot 5300||Tobelbaderstra�e 43||Premst�tten|||537660500308;319|1500|d                                                                                                                                                                 |"

    // length must be exactly 403 bytes
    // first char is the version field. it must contain "A"
    if (binary.length != 304 || !binary.startsWith("A")) {
        return undefined;
    }

    // contains 20 fields separated by pipe char
    const fields = binary.split("|");
    if (fields.length != 21) {
        return undefined;
    }

    const referenceNumber = fields[17];
    const parcelId = referenceNumber.substr(0, 11);
    const destZipCode = fields[5];
    const destCountry = fields[4];
    const trackId = calculateGlsTrackId(parcelId);

    return {
        carrier: "gls",
        type: "return",
        parcelNo: trackId ?? referenceNumber,
        destZipCode,
        destCountry,
    } as const;
}

export function parseUps1Z3ZBarcode(binary: string) {
    if (binary.substring(0, 2) !== "1Z" && binary.substring(0, 2) !== "3Z" ||
        binary.includes(".") || binary.length !== 18) {
        return undefined;
    }
    const checksum = calculateUpsChecksum(binary, 2, 17);
    const checkDigit = (Math.ceil(checksum / 10) * 10) - checksum;
    if (checkDigit !== +binary.charAt(17)) return undefined;
    console.log("Read valid UPS 1Z/3Z barcode: ", binary);
    return {
        carrier: "ups",
        parcelNo: binary,
        type: "return",
        destZipCode: "unknown",
        destCountry: "unknown",
    } as const;
}

export function parseUpsPTNBarcode(binary: string) {
    if (binary.length !== 11) return undefined;
    if (binary.charAt(0) === "4") return undefined;
    const checksum = calculateUpsChecksum(binary, 1, 10);
    const checkDigit = (Math.ceil(checksum / 10) * 10) - checksum;
    if (checkDigit !== +binary.charAt(10)) return undefined;
    console.log("Read valid UPS PTN barcode: ", binary);
    return {
        carrier: "ups",
        parcelNo: binary,
        type: "return",
        destZipCode: "unknown",
        destCountry: "unknown",
    } as const;
}

export function parseDhlNumberBarcode(binary: string) {
    if (binary.substring(0, 2) !== "00" || binary.length !== 20) {
        return undefined;
    }

    return {
        carrier: "dhl",
        parcelNo: binary.substring(2),
        type: "inbound",
        destZipCode: "unknown",
        destCountry: "unknown",
    } as const;
}

export function parseDhlAlphanumericBarcode(binary: string) {
    let res = {
        carrier: "dhl",
        parcelNo: binary.substring(2),
        type: "inbound",
        destZipCode: "unknown",
        destCountry: "unknown",
    } as const;

    if (binary.substring(0, 1) == "J" && binary.length >= 11 && binary.length <= 36) return {...res, parcelNo: binary.substring(1)};

    if ((binary.substring(0, 2) == "1J"
        || binary.substring(0, 2) == "2J"
        || binary.substring(0, 2) == "3J"
        || binary.substring(0, 2) == "4J") && binary.length >= 12 && binary.length <= 37) return res;

    if ((binary.substring(0, 2) == "5J"
        || binary.substring(0, 2) == "6J") && binary.length >= 12 && binary.length <= 22) return res;

    return undefined
}

export function parsePhysIcalContainerUrl(binary: string) {
    if(physicalAdapter.isPhysicalContainerUrl(binary)) {
        return {
            carrier: "physical",
            parcelNo: binary,
            type: "inbound",
            destZipCode: "unknown",
            destCountry: "unknown",
        } as const;
    }
}

function calculateUpsChecksum(binary: string, fromIndex: number, toIndex: number): number {
    const relevantSubstring: string = binary.substring(fromIndex, toIndex);
    let sum: number = 0;
    [...relevantSubstring].forEach((char, index) => {
        const numericChar: number = isNaN(+char) ? +upsCharToNumber(char) : +char;
        sum += index % 2 == 0 ? numericChar : numericChar * 2;
    });
    return sum;
}

function upsCharToNumber(char: string): number {
    const ascii: number = char.toUpperCase().charCodeAt(0);
    return (ascii - 63) % 10;
}